// src/services/companionService.js
// Client rail for Marlow, The Art of Story's companion — a thin wrapper over
// the DDD Knowledge Hub coaching endpoints. The KH functions are onRequest
// callables, so the caller's ID token rides the Authorization header. People
// sign in at the DDD Auth Hub, which hands this app a session on its own
// project, `ddd-art-of-story` (see services/authHub.js). That project is in the
// Knowledge Hub's TRUSTED_SPOKE_PROJECTS, so the spoke-audience token verifies
// at the third link of KH's chain.
//
// What is distinctly Marlow's: every askCoach carries a compact summary of the
// teller's Storytelling Style Profile, so she can fit the craft to the person.
// Someone strong on emotional craft and loose on structure needs the opposite
// help from someone who structures well and narrates flatly — and Marlow is
// told to use that to shape the work, never to label the person.
//
// Australian English throughout.
import { getFunctions, httpsCallableFromURL } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { moduleUserContext } from './companionModuleContext';

const APP_ID = 'art-of-story';
const BASE_URL = 'https://australia-southeast1-ddd-knowledge-hub.cloudfunctions.net';

const URLS = {
  askCoach: `${BASE_URL}/askCoach`,
  getProfile: `${BASE_URL}/getCoachingProfile`,
  updateProfile: `${BASE_URL}/updateCoachingProfile`,
  deleteProfile: `${BASE_URL}/deleteCoachingProfile`,
  exportData: `${BASE_URL}/exportCoachingData`,
  startSession: `${BASE_URL}/startCoachingSession`,
  endSession: `${BASE_URL}/endCoachingSession`,
  forgetSession: `${BASE_URL}/forgetCoachingSession`,
};

function getCallable(url) {
  try {
    // This app's own Firebase session is the sign-in; its project is a trusted
    // spoke on the Hub side, so the spoke-audience token verifies.
    return httpsCallableFromURL(getFunctions(getApp(), 'australia-southeast1'), url);
  } catch {
    return null;
  }
}

// Summarise the teller's Storytelling Style Profile for Marlow's companion
// mode. The hub whitelists `storyProfileSummary` and truncates context values
// at 200 characters, so this stays terse and plain-text:
// "The Natural Connector; strongest Emotional Craft 78; weakest Structure 34".
//
// Only the extremes are sent, not all seven dimensions: the whole scale does
// not fit the cap, and truncating mid-list would silently drop whichever
// dimension happened to sort last. Strongest and weakest are what actually
// change how Marlow works with someone.
const DIMENSION_SHORT = {
  storyAwareness: 'Awareness',
  narrativeStructure: 'Structure',
  emotionalCraft: 'Emotional Craft',
  contextAdaptation: 'Context',
  vulnerabilityCalibration: 'Vulnerability',
  deliveryPresence: 'Delivery',
  storyListening: 'Listening',
};

export const buildStoryProfileContext = (results) => {
  try {
    const r = results || JSON.parse(localStorage.getItem('artOfStory_assessmentResults'));
    if (!r) return '';

    const parts = [];
    if (r.profileType?.name) parts.push(r.profileType.name);

    const scored = Object.entries(r.dimensions || {})
      .map(([key, d]) => ({ label: DIMENSION_SHORT[key] || d?.name || key, score: d?.score }))
      .filter((d) => typeof d.score === 'number');

    if (scored.length) {
      const sorted = [...scored].sort((a, b) => b.score - a.score);
      const best = sorted[0];
      const worst = sorted[sorted.length - 1];
      parts.push(`strongest ${best.label} ${best.score}`);
      // A flat profile has no meaningful weakest dimension; saying so would
      // invite Marlow to aim at a gap that isn't there.
      if (worst.label !== best.label && best.score - worst.score >= 5) {
        parts.push(`weakest ${worst.label} ${worst.score}`);
      }
    }
    if (typeof r.avgScore === 'number') parts.push(`overall ${r.avgScore}`);

    return parts.join('; ').slice(0, 200);
  } catch {
    return '';
  }
};

// Error copy in Marlow's register — warm, plain, workshop-room, never curt.
function mapError(error) {
  const code = error?.code;
  const details = error?.customData || error?.details || {};
  if (code === 'functions/unauthenticated') {
    return { success: false, error: 'signin-required', response: 'You’ll need to sign in before we can talk.' };
  }
  if (code === 'functions/failed-precondition') {
    return { success: false, error: 'consent-required', response: 'There’s one thing to agree to before we begin.' };
  }
  if (code === 'functions/resource-exhausted') {
    return {
      success: false,
      error: 'cap-exceeded',
      dailyCapExceeded: !!details.dailyCapExceeded,
      appCapExceeded: !!details.appCapExceeded,
      tier: details.tier,
      dailyLimit: details.dailyLimit,
      upgradeUrl: details.upgradeUrl,
      response: details.appCapExceeded
        ? 'The rehearsal room is full at the moment. Please come back a little later.'
        : details.tier === 'free'
          ? 'That’s the last of today’s five free messages. It resets at midnight, and honestly, the best thing you can do between now and then is tell the story to an actual person.'
          : 'You’ve reached today’s limit. It resets at midnight.',
    };
  }
  return {
    success: false,
    error: error?.message || 'unknown',
    response: 'I’m having trouble connecting just now. Please try again in a moment.',
  };
}

class CompanionService {
  constructor() {
    this.conversationHistory = [];
    this.sessionId = null;
  }

  resetSession() {
    this.conversationHistory = [];
    this.sessionId = null;
  }

  // Module singleton, so the thread survives route changes — but not a reload,
  // and not a remount against a fresh import. snapshot/restore let the page
  // carry the thread across both (see CompanionPage's sessionStorage rail).
  snapshot() {
    return { sessionId: this.sessionId, conversationHistory: this.conversationHistory };
  }

  restore({ sessionId, conversationHistory } = {}) {
    if (sessionId) this.sessionId = sessionId;
    if (Array.isArray(conversationHistory)) this.conversationHistory = conversationHistory;
  }

  async getProfile() {
    const c = getCallable(URLS.getProfile);
    if (!c) return { success: false, error: 'not-ready' };
    try { return (await c({})).data; } catch (e) { return mapError(e); }
  }

  async grantConsent({ personalContext = {}, dataSharingScope = 'app-only' } = {}) {
    const c = getCallable(URLS.updateProfile);
    if (!c) return { success: false, error: 'not-ready' };
    try {
      return (await c({
        consent: { granted: true, retentionAccepted: true, dataSharingScope },
        personalContext,
      })).data;
    } catch (e) { return mapError(e); }
  }

  // Deletes what Marlow keeps for this app only. The Hub treats an empty
  // payload as the legacy "delete every app's coaching data" request, so an
  // Art of Story user clearing Marlow also wiped their coaching history in
  // every other Dive Deep Dialogues app. Relationship Compass sends appId too.
  async deleteAll() {
    const c = getCallable(URLS.deleteProfile);
    if (!c) return { success: false, error: 'not-ready' };
    try {
      const r = (await c({ appId: APP_ID })).data;
      this.resetSession();
      return r;
    } catch (e) { return mapError(e); }
  }

  async exportData() {
    const c = getCallable(URLS.exportData);
    if (!c) return { success: false, error: 'not-ready' };
    try { return (await c({})).data; } catch (e) { return mapError(e); }
  }

  async startSession({ topic = null, sessionType = 'exploration' } = {}) {
    const c = getCallable(URLS.startSession);
    if (!c) return { success: false, error: 'not-ready' };
    try {
      const r = (await c({ appId: APP_ID, topic, sessionType })).data;
      if (r?.sessionId) this.sessionId = r.sessionId;
      this.conversationHistory = [];
      return r;
    } catch (e) { return mapError(e); }
  }

  async endSession({ rating = null } = {}) {
    if (!this.sessionId) return { success: true };
    const c = getCallable(URLS.endSession);
    if (!c) return { success: false, error: 'not-ready' };
    try {
      const r = (await c({
        sessionId: this.sessionId, appId: APP_ID, rating, transcript: this.conversationHistory,
      })).data;
      this.resetSession();
      return r;
    } catch (e) { return mapError(e); }
  }

  async askCoach(question, { mode = 'coaching', module = null, returnUrl = null, storyProfile = null } = {}) {
    const c = getCallable(URLS.askCoach);
    if (!c) {
      return { success: false, error: 'not-ready', response: 'Please sign in before we talk.' };
    }
    try {
      const profileSummary = buildStoryProfileContext(storyProfile);
      const r = (await c({
        question,
        appId: APP_ID,
        conversationHistory: this.conversationHistory,
        sessionId: this.sessionId,
        mode,
        returnUrl,
        userContext: {
          // The module the teller came from, so Marlow knows what they were
          // reading (rendered by the Knowledge Hub's module context).
          ...moduleUserContext(module),
          ...(profileSummary ? { storyProfileSummary: profileSummary } : {}),
        },
      })).data;
      if (r.success) {
        if (r.sessionId) this.sessionId = r.sessionId;
        this.conversationHistory.push(
          { role: 'user', content: question },
          { role: 'assistant', content: r.response },
        );
        if (this.conversationHistory.length > 30) {
          this.conversationHistory = this.conversationHistory.slice(-30);
        }
      }
      return r;
    } catch (e) { return mapError(e); }
  }

  async forgetSession(sessionId) {
    const c = getCallable(URLS.forgetSession);
    if (!c) return { success: false, error: 'not-ready' };
    try { return (await c({ sessionId })).data; } catch (e) { return mapError(e); }
  }
}

const companionService = new CompanionService();
export default companionService;
export { CompanionService, APP_ID };
